/**
 * mescreen mitochondrial-health quiz.
 * 5 questions, each scored 0-3. Total 0-15. Three result tiers with
 * distinct messaging. Hand-off to the chat agent carries the score as the
 * first user message so the agent can respond contextually.
 *
 * Triggered from anywhere via `window.mescreen.openQuiz()`, or by
 * visiting any URL with the `#quiz` hash.
 */
(function () {
  "use strict";
  if (window.__mescreenQuizLoaded) return;
  window.__mescreenQuizLoaded = true;

  var QUESTIONS = [
    {
      q: "How's your energy through the day?",
      options: [
        { label: "Steady — I feel good all day", score: 3 },
        { label: "Good morning, afternoon crash", score: 2 },
        { label: "Tired most of the day", score: 1 },
        { label: "Exhausted even after a full night's sleep", score: 0 },
      ],
    },
    {
      q: "How well do you recover from exercise or physical activity?",
      options: [
        { label: "Bounce back quickly, feel energized", score: 3 },
        { label: "Normal recovery", score: 2 },
        { label: "Takes longer than it used to", score: 1 },
        { label: "Sore for days — I avoid hard activity", score: 0 },
      ],
    },
    {
      q: "How often do you experience brain fog or memory lapses?",
      options: [
        { label: "Rarely", score: 3 },
        { label: "A few times a month", score: 2 },
        { label: "A few times a week", score: 1 },
        { label: "Most days", score: 0 },
      ],
    },
    {
      q: "How's your sleep quality?",
      options: [
        { label: "Deep, restorative, wake up rested", score: 3 },
        { label: "Decent most nights", score: 2 },
        { label: "Often disrupted or light", score: 1 },
        { label: "Wake up tired no matter what", score: 0 },
      ],
    },
    {
      q: "How would you describe your overall vitality right now?",
      options: [
        { label: "I feel like I'm thriving", score: 3 },
        { label: "Managing okay", score: 2 },
        { label: "Running on empty", score: 1 },
        { label: "Burnt out", score: 0 },
      ],
    },
  ];

  function interpret(total) {
    if (total >= 12) {
      return {
        tier: "optimal",
        title: "Your self-reported energy profile looks strong.",
        body:
          "You answered in the top tier — your baseline feels good. mescreen is still valuable here: it quantifies your cellular energy system so you can track improvements over time and catch any drift early. This is where longevity-focused people usually start.",
      };
    }
    if (total >= 7) {
      return {
        tier: "moderate",
        title: "There are signals worth investigating.",
        body:
          "Your answers suggest your mitochondrial function may not be at full capacity. A functional test like mescreen measures what's actually happening in your cells — so you can target the right interventions instead of guessing.",
      };
    }
    return {
      tier: "concerning",
      title: "Your mitochondria may be under real stress.",
      body:
        "The pattern in your answers — low energy, slow recovery, brain fog, poor sleep — is exactly what mitochondrial inefficiency looks like. Testing gives you and your doctor a measurable picture of what's happening at the cellular level, and points at what to do about it.",
    };
  }

  var CHECKOUT_URL =
    "https://mescreen.com/products/mescreen-mitochondrial-function-test-healthcare-provider-consult";

  var state = { index: 0, answers: [] };
  var overlay = null;
  var contentEl = null;

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function mount() {
    if (overlay) return;
    overlay = document.createElement("div");
    overlay.className = "mes-quiz-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-label", "mescreen mitochondrial health quiz");
    overlay.innerHTML =
      '<div class="mes-quiz-modal">' +
      '<button class="mes-quiz-close" aria-label="Close quiz" type="button">&times;</button>' +
      '<div class="mes-quiz-content"></div>' +
      "</div>";
    document.body.appendChild(overlay);
    contentEl = overlay.querySelector(".mes-quiz-content");

    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) close();
    });
    overlay.querySelector(".mes-quiz-close").addEventListener("click", close);
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && overlay.classList.contains("mes-quiz-open")) {
        close();
      }
    });
  }

  function renderQuestion() {
    var q = QUESTIONS[state.index];
    var progress = (state.index / QUESTIONS.length) * 100;
    var html =
      '<div class="mes-quiz-progress"><div class="mes-quiz-progress-bar" style="width: ' +
      progress +
      '%"></div></div>' +
      '<div class="mes-quiz-stepnum">Question ' +
      (state.index + 1) +
      " of " +
      QUESTIONS.length +
      "</div>" +
      '<h2 class="mes-quiz-title">' +
      escapeHtml(q.q) +
      "</h2>" +
      '<div class="mes-quiz-options">';
    q.options.forEach(function (opt, i) {
      html +=
        '<button type="button" class="mes-quiz-option" data-score="' +
        opt.score +
        '" data-index="' +
        i +
        '">' +
        escapeHtml(opt.label) +
        "</button>";
    });
    html += "</div>";
    contentEl.innerHTML = html;
    contentEl.querySelectorAll(".mes-quiz-option").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var score = parseInt(btn.getAttribute("data-score"), 10);
        state.answers.push(score);
        state.index++;
        transitionTo(state.index < QUESTIONS.length ? renderQuestion : renderResults);
      });
    });
  }

  function renderResults() {
    var total = state.answers.reduce(function (a, b) { return a + b; }, 0);
    var result = interpret(total);

    var contextSummary =
      "I just completed the mescreen quiz and scored " +
      total +
      "/15 (tier: " +
      result.tier +
      "). My answers by question: " +
      state.answers
        .map(function (s, i) { return "Q" + (i + 1) + "=" + s + "/3"; })
        .join(", ") +
      ". What does this suggest for me?";

    contentEl.innerHTML =
      '<div class="mes-quiz-progress"><div class="mes-quiz-progress-bar" style="width: 100%"></div></div>' +
      '<div class="mes-quiz-result mes-quiz-result-' + result.tier + '">' +
      '<div class="mes-quiz-score">' + total + "<span>/15</span></div>" +
      '<h2 class="mes-quiz-result-title">' + escapeHtml(result.title) + "</h2>" +
      '<p class="mes-quiz-result-body">' + escapeHtml(result.body) + "</p>" +
      '<div class="mes-quiz-actions">' +
        '<button type="button" class="mes-quiz-btn mes-quiz-btn-primary" data-action="chat">' +
          "💬 Chat with Mia about your results" +
        "</button>" +
        '<a href="' + CHECKOUT_URL + '" class="mes-quiz-btn mes-quiz-btn-secondary">' +
          "Order your test — $699" +
        "</a>" +
      "</div>" +
      '<div class="mes-quiz-lead">' +
        "<p>Or save your results — we'll email them over:</p>" +
        '<form name="quiz-lead" class="mes-quiz-email-form" autocomplete="on">' +
          '<input type="email" placeholder="your@email.com" required autocomplete="email">' +
          '<button type="submit" class="mes-quiz-btn mes-quiz-btn-outline">Email my results</button>' +
        "</form>" +
        '<div class="mes-quiz-email-confirm" style="display:none;">' +
          "✓ Saved — Mia will pick it up from here." +
        "</div>" +
      "</div>" +
    "</div>";

    // Chat handoff — close quiz, open chat, send context as the first message.
    contentEl.querySelector('[data-action="chat"]').addEventListener("click", function () {
      close();
      setTimeout(function () {
        if (window.mescreen && typeof window.mescreen.openAndSend === "function") {
          window.mescreen.openAndSend(contextSummary);
        }
      }, 320);
    });

    // Email capture — funnel into the chat agent so the existing regex
    // extractor and conversation log pick it up.
    var form = contentEl.querySelector(".mes-quiz-email-form");
    var confirm = contentEl.querySelector(".mes-quiz-email-confirm");
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var email = form.querySelector('input[type="email"]').value.trim();
      if (!email) return;
      var msg =
        "Quiz complete: " +
        total +
        "/15 (" +
        result.tier +
        "). Please email my results to " +
        email +
        " when you can.";
      form.style.display = "none";
      confirm.style.display = "block";
      setTimeout(function () {
        close();
        setTimeout(function () {
          if (window.mescreen && typeof window.mescreen.openAndSend === "function") {
            window.mescreen.openAndSend(msg);
          }
        }, 320);
      }, 900);
    });
  }

  function transitionTo(renderFn) {
    if (!contentEl) return;
    contentEl.style.opacity = "0";
    setTimeout(function () {
      renderFn();
      contentEl.style.opacity = "1";
    }, 180);
  }

  function open() {
    mount();
    state.index = 0;
    state.answers = [];
    overlay.classList.add("mes-quiz-open");
    document.body.style.overflow = "hidden";
    renderQuestion();
  }

  function close() {
    if (!overlay) return;
    overlay.classList.remove("mes-quiz-open");
    document.body.style.overflow = "";
  }

  // Public API.
  window.mescreen = window.mescreen || {};
  window.mescreen.openQuiz = open;
  window.mescreen.closeQuiz = close;

  // URL-hash trigger: /#quiz opens the quiz on load + whenever the hash
  // changes, so you can link to it from emails, nav items, etc.
  function handleHash() {
    if (window.location.hash === "#quiz") open();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", handleHash);
  } else {
    handleHash();
  }
  window.addEventListener("hashchange", handleHash);
})();
